import { useMemo } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import { PlayWalletStatus, getPlayWalletStatus } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'

/**
 * The user's linked wallets and how the wallet connected in the browser
 * relates to them: it plays when it is linked. `status` is null while there
 * is no signed-in user or no connected wallet.
 */
export const usePlayWallet = () => {
  const user = useReactiveVar(userVar)
  const { publicKey } = useWallet()
  const connectedAddress = publicKey?.toBase58() ?? null

  // NOTE: the backend lists wallets in link order; the primary one leads here
  const wallets = useMemo(
    () =>
      [...(user?.wallets ?? [])].sort(
        (a, b) => Number(b.isPrimary) - Number(a.isPrimary)
      ),
    [user?.wallets]
  )
  const linkedAddresses = useMemo(
    () => wallets.map(wallet => wallet.address),
    [wallets]
  )

  const status: PlayWalletStatus | null =
    user && connectedAddress ?
      getPlayWalletStatus(connectedAddress, linkedAddresses)
    : null

  return {
    wallets,
    linkedAddresses,
    connectedAddress,
    /** The connected wallet while it is linked: the one that plays. */
    playAddress: status === PlayWalletStatus.ready ? connectedAddress : null,
    status
  }
}
